/**
 * What on a memo needs a person — the one derivation both the run page's Gaps
 * tab and the workflow's Needs review tab list from.
 *
 * Four kinds, in the order a reviewer should meet them:
 *   blocks    a blocking field nobody answered. "Transaction type or raise
 *             size" is one item PER FIELD while neither is answered, and
 *             the server's `counts_from` counts them the same way (DOP-658),
 *             so the header, the deals table and this list say one number.
 *             Answering either lifts the block on both.
 *   required  a section set to appear in every memo whose body is empty: the
 *             run left it out and nobody has written it (DOP-657), or a
 *             person removed every paragraph in edit mode. It blocks review, as a
 *             blocking field does, and is answered by writing it in edit mode.
 *   conflict  two sources answered one fact differently; both are kept.
 *   stale     a choice or an entry made on an earlier version, and the
 *             materials have since gained a value (DOP-577): the choice
 *             still applies, and a person should look again.
 *   gap       a required or optional field that reads "Not found".
 *   omitted   a section the materials could not support, set to appear only
 *             if the files cover it. It does not block.
 *
 * Every item carries the diligence question the run wrote for it, found by
 * fact key or section key, so the table can show what the company will be asked.
 * `where` names every memo section the item touches; the questions themselves
 * all land in the one diligence section.
 */

import {
	type DealScreeningFact,
	type DealScreeningQuestion,
	type DealScreeningRun,
	type DealScreeningSection,
	EMPTIED_IN_EDIT_MODE,
	requiredSectionsMissing,
} from "../../types/dealScreening";
import { formatFactValue, formatSourceLabel, unconvertedCurrency } from "./dealScreeningFormat";

export type ReviewKind = "blocks" | "required" | "conflict" | "stale" | "gap" | "omitted";

/** The kinds that stop "Mark reviewed". */
export const blocksReview = (item: { kind: ReviewKind }): boolean =>
	item.kind === "blocks" || item.kind === "required";

export interface ReviewItem {
	/** Stable within one run version. */
	id: string;
	kind: ReviewKind;
	/** "Transaction type or raise size", "Revenue FY2025A: $13.2mm vs $13.6mm". */
	title: string;
	/** Where in the memo the item shows: "Header block · Transaction Overview". */
	where: string;
	/** Where the run looked (a gap) or which sources disagree (a conflict). */
	detail: string | null;
	question: DealScreeningQuestion | null;
	/** The facts a resolution writes to. */
	facts: DealScreeningFact[];
	section: DealScreeningSection | null;
	/** A block group's other fields, whose answer also lifts this block (DOP-643). */
	alsoAnswers?: string[];
}

export const REVIEW_KIND_LABEL: Record<ReviewKind, string> = {
	blocks: "Blocks",
	required: "Blocks",
	conflict: "Conflict",
	stale: "Review choice",
	gap: "Not found",
	omitted: "Omitted",
};

const factId = (fact: DealScreeningFact): string =>
	fact.period ? `${fact.key}@${fact.period}` : fact.key;

const factTitle = (fact: DealScreeningFact): string =>
	fact.period ? `${fact.label} ${fact.period}` : fact.label;

/** The titles of the sections a fact's value appears in, header block first. */
function whereUsed(run: DealScreeningRun, facts: DealScreeningFact[]): string {
	const keys = new Set(facts.map((fact) => fact.key));
	const places: string[] = [];
	if (facts.some((fact) => fact.kind === "header")) places.push("Header block");
	if (facts.some((fact) => fact.kind !== "header")) {
		const summary = run.sections.find((s) => s.key === "financial_summary");
		places.push(summary?.title ?? "Financial Summary");
	}
	for (const section of run.sections) {
		const cites = section.paragraphs.some((paragraph) =>
			paragraph.sentences.some((sentence) =>
				sentence.fact_keys.some((key) => keys.has(key)),
			),
		);
		if (cites && !places.includes(section.title)) places.push(section.title);
	}
	return places.join(" · ");
}

function questionFor(
	run: DealScreeningRun,
	match: (question: DealScreeningQuestion) => boolean,
): DealScreeningQuestion | null {
	return run.questions.find(match) ?? null;
}

/**
 * Whether a gap needs a value for each year, not one (DOP-650): a by-year
 * figure the run found for no year is stored as one gap with no period, and
 * one value cannot answer it. It is filled in the financial summary instead.
 */
export function needsEachYear(run: DealScreeningRun, fact: DealScreeningFact): boolean {
	return (
		fact.period === null &&
		fact.kind !== "header" &&
		(run.figures ?? []).some((figure) => figure.key === fact.key && figure.periods === "by_year")
	);
}

export function reviewItems(run: DealScreeningRun): ReviewItem[] {
	const sorted = [...run.facts].sort((a, b) => a.sort_order - b.sort_order);

	// ------------------------------------------------------------ blocks
	const groups = new Map<string, DealScreeningFact[]>();
	for (const fact of sorted) {
		if (fact.policy !== "blocks" || fact.kind === "derived") continue;
		const group = fact.block_group ?? fact.key;
		groups.set(group, [...(groups.get(group) ?? []), fact]);
	}
	const blocks: ReviewItem[] = [];
	const inOpenGroup = new Set<string>();
	groups.forEach((members, group) => {
		if (!members.every((fact) => fact.state === "gap")) return;
		// One gap per field (DOP-643): transaction type and raise size are
		// different answers. Either still lifts the block, and each says so.
		members.forEach((fact) => {
			inOpenGroup.add(factId(fact));
			blocks.push({
				id: `blocks:${group}:${factId(fact)}`,
				kind: "blocks",
				title: factTitle(fact),
				where: whereUsed(run, [fact]),
				detail: fact.gap_reason ?? null,
				question: questionFor(run, (question) => question.fact_key === fact.key),
				facts: [fact],
				section: null,
				alsoAnswers: members.filter((other) => other !== fact).map((other) => other.label),
			});
		});
	});

	// ------------------------------------------------------------ conflicts
	const conflicts: ReviewItem[] = sorted
		.filter((fact) => fact.conflict === "open")
		.map((fact) => {
			const values = [
				formatFactValue(fact.value, fact.unit, unconvertedCurrency(fact)),
				...fact.alternates.map((alt) =>
					formatFactValue(alt.value, alt.unit, unconvertedCurrency(alt)),
				),
			];
			const sources = [
				fact.source,
				...fact.alternates.map((alt) => alt.source),
			].filter((source): source is NonNullable<typeof source> => source !== null);
			return {
				id: `conflict:${factId(fact)}`,
				kind: "conflict" as const,
				title: `${factTitle(fact)}: ${values.join(" vs ")}`,
				where: whereUsed(run, [fact]),
				detail:
					[
						fact.decision_status === "orphaned" ? (fact.stale_reason ?? null) : null,
						sources.map(formatSourceLabel).join(" · ") || null,
					]
						.filter((part): part is string => part !== null && part !== "")
						.join(" — ") || null,
				question: questionFor(
					run,
					(question) =>
						question.fact_key === fact.key && question.origin === "conflict",
				),
				facts: [fact],
				section: null,
			};
		});

	// ------------------------------------------------------------ stale decisions
	const stale: ReviewItem[] = sorted
		.filter((fact) => fact.decision_status === "stale" && fact.conflict !== "open")
		.map((fact) => ({
			id: `stale:${factId(fact)}`,
			kind: "stale" as const,
			title: `${factTitle(fact)}: ${
				fact.state === "not_available"
					? "accepted as not found"
					: formatFactValue(fact.value, fact.unit, unconvertedCurrency(fact))
			}`,
			where: whereUsed(run, [fact]),
			detail: fact.stale_reason ?? null,
			question: null,
			facts: [fact],
			section: null,
		}));

	// ------------------------------------------------------------ gaps
	const gaps: ReviewItem[] = sorted
		.filter((fact) => fact.state === "gap" && !inOpenGroup.has(factId(fact)))
		.map((fact) => ({
			id: `gap:${factId(fact)}`,
			kind: "gap" as const,
			title: factTitle(fact),
			where: whereUsed(run, [fact]),
			detail: fact.gap_reason,
			question: questionFor(run, (question) => question.fact_key === fact.key),
			facts: [fact],
			section: null,
		}));

	// ------------------------------------------------------------ omitted
	const inOrder = [...run.sections].sort((a, b) => a.order - b.order);
	const mustWrite = new Set(requiredSectionsMissing(inOrder).map((section) => section.key));
	const leftOut = inOrder.filter(
		(section) => section.status === "omitted" || mustWrite.has(section.key),
	);
	const sectionItem = (section: DealScreeningSection): ReviewItem => {
		const required = mustWrite.has(section.key);
		return {
			id: `${required ? "required" : "omitted"}:${section.key}`,
			kind: required ? "required" : "omitted",
			title: section.title,
			where: required ? "Set to appear in every memo" : "Left out of the memo",
			// A section the run wrote has no reason it was left out: a person emptied it.
			detail: section.status === "omitted" ? section.omitted_reason : EMPTIED_IN_EDIT_MODE,
			question: questionFor(
				run,
				(question) => question.section_key === section.key,
			),
			facts: [],
			section,
		};
	};
	const required = leftOut.filter((section) => mustWrite.has(section.key)).map(sectionItem);
	const omitted = leftOut.filter((section) => !mustWrite.has(section.key)).map(sectionItem);

	return [...blocks, ...required, ...conflicts, ...stale, ...gaps, ...omitted];
}
